import { supabase } from '../lib/supabase'
import { AircraftAcknowledgment, AircraftAcknowledgmentInput, ChangeType } from '../types/database'

export class AircraftAcknowledgmentsService {
  private static readonly TABLE_NAME = 'aircraft_acknowledgments'

  /**
   * Create a new acknowledgment
   */
  static async create(acknowledgmentInput: AircraftAcknowledgmentInput): Promise<AircraftAcknowledgment> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .insert({
          aircraft_id: acknowledgmentInput.aircraft_id,
          change_type: acknowledgmentInput.change_type,
          previous_value: acknowledgmentInput.previous_value,
          new_value: acknowledgmentInput.new_value,
          acknowledged_by: (await supabase.auth.getUser()).data.user?.id
        })
        .select()
        .single()

      if (error) {
        throw new Error(`Failed to create acknowledgment: ${error.message}`)
      }

      return data
    } catch (error) {
      throw error
    }
  }

  /**
   * Get acknowledgments for a specific aircraft
   */
  static async getByAircraftId(aircraftId: number): Promise<AircraftAcknowledgment[]> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select('*')
        .eq('aircraft_id', aircraftId)
        .order('acknowledged_at', { ascending: false })

      if (error) {
        throw new Error(`Failed to fetch acknowledgments: ${error.message}`)
      }

      return data || []
    } catch (error) {
      throw error
    }
  }

  /**
   * Get the latest acknowledgment for a specific aircraft and change type
   */
  static async getLatestByType(aircraftId: number, changeType: ChangeType): Promise<AircraftAcknowledgment | null> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select('*')
        .eq('aircraft_id', aircraftId)
        .eq('change_type', changeType)
        .order('acknowledged_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (error) {
        throw new Error(`Failed to fetch latest acknowledgment: ${error.message}`)
      }

      return data
    } catch (error) {
      throw error
    }
  }

  /**
   * Check if a change has been acknowledged since a specific date
   */
  static async isAcknowledgedSince(
    aircraftId: number, 
    changeType: ChangeType, 
    sinceDate: string
  ): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select('id')
        .eq('aircraft_id', aircraftId)
        .eq('change_type', changeType)
        .gte('acknowledged_at', sinceDate)
        .limit(1)

      if (error) {
        return false
      }

      return (data?.length || 0) > 0
    } catch (error) {
      return false
    }
  }

  /**
   * Get bulk acknowledgment status for multiple aircraft
   */
  static async getBulkAcknowledmentStatus(
    aircraftIds: number[]
  ): Promise<Map<number, { price_change?: string; new_to_market?: string }>> {
    if (aircraftIds.length === 0) return new Map()

    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select('aircraft_id, change_type, acknowledged_at')
        .in('aircraft_id', aircraftIds)
        .order('acknowledged_at', { ascending: false })

      if (error) {
        return new Map()
      }

      const statusMap = new Map<number, { price_change?: string; new_to_market?: string }>()

      // Group by aircraft_id and get latest acknowledgment for each change type
      for (const record of data || []) {
        const aircraftId = record.aircraft_id
        const existing = statusMap.get(aircraftId) || {}

        if (record.change_type === 'price_change' && !existing.price_change) {
          existing.price_change = record.acknowledged_at
        } else if (record.change_type === 'new_to_market' && !existing.new_to_market) {
          existing.new_to_market = record.acknowledged_at
        }

        statusMap.set(aircraftId, existing)
      }

      return statusMap
    } catch (error) {
      return new Map()
    }
  }

  /**
   * Delete acknowledgments for a specific aircraft (admin function)
   */
  static async deleteByAircraftId(aircraftId: number): Promise<void> {
    try {
      const { error } = await supabase
        .from(this.TABLE_NAME)
        .delete()
        .eq('aircraft_id', aircraftId)

      if (error) {
        throw new Error(`Failed to delete acknowledgments: ${error.message}`)
      }
    } catch (error) {
      throw error
    }
  }

  /**
   * Get acknowledgment statistics for a user
   */
  static async getUserStatistics(userId?: string): Promise<{
    total: number
    priceChanges: number
    newListings: number
    last30Days: number
  }> {
    try {
      let query = supabase
        .from(this.TABLE_NAME)
        .select('change_type, acknowledged_at')

      if (userId) {
        query = query.eq('acknowledged_by', userId)
      }

      const { data, error } = await query

      if (error) {
        return { total: 0, priceChanges: 0, newListings: 0, last30Days: 0 }
      }

      const thirtyDaysAgo = new Date()
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

      let priceChanges = 0
      let newListings = 0
      let last30Days = 0

      for (const record of data || []) {
        if (record.change_type === 'price_change') priceChanges++
        if (record.change_type === 'new_to_market') newListings++
        if (new Date(record.acknowledged_at) >= thirtyDaysAgo) last30Days++
      }

      return {
        total: data?.length || 0,
        priceChanges,
        newListings,
        last30Days
      }
    } catch (error) {
      return { total: 0, priceChanges: 0, newListings: 0, last30Days: 0 }
    }
  }

  /**
   * Get recent acknowledgments across all aircraft
   */
  static async getRecent(limit = 50): Promise<AircraftAcknowledgment[]> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select('*')
        .order('acknowledged_at', { ascending: false })
        .limit(limit)

      if (error) {
        throw new Error(`Failed to fetch recent acknowledgments: ${error.message}`)
      }

      return data || []
    } catch (error) {
      throw error
    }
  }
}

export default AircraftAcknowledgmentsService
